import { Component } from 'react'
import gql from 'graphql-tag'
import { graphql } from '@apollo/client/react/hoc'
import { Alert, Progress, Nav, NavItem, NavLink, TabContent, TabPane, UncontrolledTooltip, Button, CardBody, Card, CardHeader } from 'reactstrap'
import { Link } from "react-router-dom"

import './Incidents.css'
import OverviewTab from './OverviewTab/OverviewTab'
import IncidentDocumentsTab from './DocumentsTab/IncidentDocumentsTab'
import PartiesTabContainer from './PartiesTab/PartiesTabContainer'
import ClaimsTab from './ClaimsTab/ClaimsTab'
import InspectionTab from './InspectionTab/InspectionTab'
import { HasPermissionsQuery } from '../Functions/querys'
import CopyToClipboard from '../Material/CopyToClipboard'
import withApolloClient from '../withApolloClient'
import DatetimeRenderer from "../Material/DatetimeRenderer"
import DashcamContainer from '../Material/DashcamContainer'
import ProgressTrackingBar from '../Material/ProgressTrackingBar'
import HistoryList from '../Material/HistoryList'
import TasksTab from './TasksTab/TasksTab'
import ConfirmationPopup from '../Material/ConfirmationPopup'
import ArchiveReasonModal from './IncidentModals/ArchiveReasonModal'
import toTitleCase from "../Functions/toTitleCase"

const IncidentDetailQuery = gql`query Incident($incidentId: ID!){
    incident(incidentId:$incidentId){
        id
        pk
        incidentDate
        location
        dataProgress
        status
        carOnHold
        isArchived
        title
        metadata
        isRecordOnly
        missingDataFields
        openTaskCount
        taskSet(isArchived:false){
            edges{
                node{
                    id
                    status
                }
            }
        }
        branch{
            id
            name
        }
        damageTransaction {
            id
        }
        incidentdamageSet {
            edges {
                node {
                    id
                }
            }
        }
        carCondition{
            id
        }
        rental{
            id
            driver{
                id
                name
                hasPendingInsurance
                hasIncompleteReturn
            }
        }
        car{
            id
            pk
            year
            vin
            carModel{
                id
                name
                make
            }
        }
        subrogationclaimSet(isArchived: false) {
          edges {
            node {
              id
              dateAdded
              externalId
            }
          }
        }
        liabilityclaimSet(isArchived: false) {
          edges {
            node {
              id
              dateAdded
              externalId
            }
          }
        }
        incidentdocumentSet(isArchived: false) {
            edges {
                node {
                    id
                }
            }
        }
    }
    statusChoices: __type(name: "IncidentsIncidentStatusChoices") {
        enumValues {
            name
            description
        }
    }
}`

const DeleteIncidentMutation = gql`mutation DeleteIncidentMutation($input: DeleteIncidentMutationInput!){
    deleteIncident(input:$input){
        ok
        errors{
            messages
        }
    }
} `

const UpdateIncidentMutation = gql`mutation UpdateIncidentMutation($input: UpdateIncidentMutationInput!){
    updateIncident(input:$input){
        ok
        errors{
            messages
        }
    }
} `

const INCIDENT_DETAIL_PAGE_TABS = [
    { id: "#overview", name: "OVERVIEW" },
    { id: "#inspection", name: "INSPECTION" },
    { id: "#parties", name: "PARTIES" },
    { id: "#claims", name: "CLAIMS" },
    { id: "#documents", name: "DOCUMENTS" },
    { id: "#dashcam", name: "DASHCAM" },
    { id: "#tasks", name: "TASKS" },
    { id: "#history", name: "HISTORY" },
]
class IncidentDetailContainer extends Component {
    constructor(props) {
        super(props)
        this.state = {
            tab: "",
            loading: false,
            error: "",
            openModal: "",
            showArchiveConfirm: false,
            archiveReason: "",
            showFullArchiveReason: false,
        }
    }

    toggleTab = (tabId) => {
        this.props.navigate({ hash: tabId })
        this.setState({ tab: tabId })
    }

    toggleModal = (modalName) => {
        const isArchiveModal = modalName === "archiveIncident"
        const isOpen = this.state.openModal === modalName
        const isArchived = this.props.incident?.isArchived
        this.setState({
            openModal: isOpen ? "" : modalName,
            ...(isArchiveModal && {
                showArchiveConfirm: isArchived,
                archiveReason: "",
            }),
        })
    };

    handleArchiveReasonContinue = (reason) => {
        this.setState({ showArchiveConfirm: true, archiveReason: reason || "", })
    }

    toggleArchiveReasonView = () => {
        this.setState(prev => ({
            showFullArchiveReason: !prev.showFullArchiveReason
        }))
    }

    deleteIncident = (incidentId) => {
        try {
            if (incidentId) {
                this.setState({ loading: true })
                let input = {
                    incidentId: incidentId,
                }
                this.props.apolloClient.mutate({
                    mutation: DeleteIncidentMutation,
                    variables: { input },
                }).then((result) => {
                    if (result && result.data && result.data.deleteIncident && result.data.deleteIncident.ok) {
                        this.setState({ errorMessage: "", successMessage: "Successfully Deleted Incident" })
                        setTimeout(() => {
                            window.location.href = "/incidents"
                        }, 1500)
                    } else {
                        let errorMessage = "An error occurred, could not complete request."
                        if (result.data && result.data.deleteIncident && result.data.deleteIncident.errors && result.data.deleteIncident.errors[0] && result.data.deleteIncident.errors[0].messages) {
                            errorMessage = result.data.deleteIncident.errors[0].messages.toString()
                        }
                        this.setState({ error: errorMessage })
                    }
                    this.setState({ loading: false })
                }).catch((err) => {
                    let errorMessage = "An error has occurred"
                    this.setState({ error: errorMessage, loading: false })
                })
            }
            else this.setState({ error: "Missing Required Field" })
        }
        catch (err) {
            let errorMessage = "An error has occurred: " + err.toString()
            this.setState({ error: errorMessage, loading: false })
        }
    }

    toggleArchiveIncident = (incidentId, isArchived) => {
        try {
            if (!incidentId) {
                this.setState({ error: "Missing Required Field" })
                return
            }
            this.setState({ loading: true })
            let input = {
                incidentId,
                isArchived: !isArchived,
            }
            if (!isArchived && this.state.archiveReason) {
                input.archiveReason = this.state.archiveReason.trim()
            }
            this.props.apolloClient.mutate({
                mutation: UpdateIncidentMutation,
                variables: { input },
            }).then((result) => {
                if (result?.data?.updateIncident?.ok) {
                    this.setState({ error: "", successMessage: isArchived ? "Successfully Un-Archived Incident" : "Successfully Archived Incident", })
                    this.toggleModal("archiveIncident")
                    setTimeout(() => {
                        window.location.reload()
                    }, 1500)
                } else {
                    let errorMessage = "An error occurred, could not complete request."
                    if (result.data && result.data.updateIncident && result.data.updateIncident.errors && result.data.updateIncident.errors[0] && result.data.updateIncident.errors[0].messages) {
                        errorMessage = result.data.updateIncident.errors[0].messages.toString()
                    }
                    this.setState({ error: errorMessage })
                }
                this.setState({ loading: false, archiveReason: "", showArchiveConfirm: false })
            }).catch((err) => {
                this.setState({ error: "An error has occurred: " + err.toString(), loading: false })
            })
        } catch (err) {
            this.setState({ error: "An error has occurred: " + err.toString(), loading: false })
        }
    }

    componentDidMount() {
        let defaultTab = INCIDENT_DETAIL_PAGE_TABS[0].id
        // Extract only the hash part without query params (e.g., '#tasks' from '#tasks/?task_id=...')
        const hashWithoutQuery = window.location.hash.split('?')[0].replace(/\/$/, '') // Also remove trailing slash if present
        if (hashWithoutQuery && INCIDENT_DETAIL_PAGE_TABS.map(item => item.id).includes(hashWithoutQuery)) {
            this.setState({ tab: hashWithoutQuery })
        } else {
            window.location.hash = defaultTab
            this.setState({ tab: defaultTab })
        }
    }

    componentDidUpdate(_, prevState) {
        if (this.state.tab !== prevState.tab) {
            this.props.navigate({ hash: this.state.tab, })
        }
    }

    fetchMetaData = (metadata = null, name) => {
        if (!metadata || metadata === "")
            return null
        try {
            metadata = JSON.parse(metadata.replace(/'/g, '"'))
        } catch {
            metadata = null
        }
        return metadata && metadata[name] ? metadata[name] : null
    }

    render() {
        if (!this.props.incident && this.props.loading) {
            return <Progress animated color="info" value="100" />
        }
        const liabilityClaims = this.props.incident?.liabilityclaimSet?.edges?.map(({ node }) => node) || []
        const subrogationClaims = this.props.incident?.subrogationclaimSet?.edges?.map(({ node }) => node) || []
        const latestLiabilityClaim = liabilityClaims.filter(claim => claim?.dateAdded).sort((a, b) => new Date(b.dateAdded) - new Date(a.dateAdded))[0] || liabilityClaims[0] || null
        const latestSubrogationClaim = subrogationClaims.filter(claim => claim?.dateAdded).sort((a, b) => new Date(b.dateAdded) - new Date(a.dateAdded))[0] || subrogationClaims[0] || null

        return (
            <div className="incident-detail-container">
                {this.state.error && <Alert color="danger">{this.state.error}</Alert>}
                <Card className="incident-card">
                    <CardHeader className="card-header-section">
                        <div className="header-wrapper">
                            <div>
                                <div className="title-row">
                                    <i className="fa fa-car car-icon" />
                                    <h2 className="vehicle-title">
                                        <Link to={`/car/${this.props.incident?.car?.pk}`} target="_blank" className="incident-detail-link">
                                            {this.props.incident?.car?.year}{" "}
                                            {toTitleCase(this.props.incident?.car?.carModel?.make)}{" "}
                                            {this.props.incident?.car?.carModel?.name}
                                            <i className="pl-2 fa fa-external-link" />
                                        </Link>
                                    </h2>
                                </div>
                                <div className="badges-row">
                                    <p className="bos-badge bos-badge-blue">{this.props.incident?.branch?.name || "No Branch"}</p>
                                    {(this.props.incident?.status && !this.props.incident?.isRecordOnly) && <>
                                        <p id="incidentStatus" className={`mb-0 custom-badge ${this.props.incident?.status === "COMPLETED" ? "success" : this.props.incident?.status === "WORKING_ON_IT" ? "warning" : this.props.incident?.status === "PROCESSED" ? "dark" : "secondary"}`} >{this.props.incident?.status ? this.props.statusChoices?.enumValues.find(item => item.name === this.props.incident.status)?.description : "--"}</p>
                                        <UncontrolledTooltip target="incidentStatus">Incident Status</UncontrolledTooltip>
                                    </>}
                                    {this.props.incident?.carOnHold && <p className='mb-0 custom-badge danger'>CAR ON HOLD</p>}
                                    {this.props.incident?.rental?.driver?.hasPendingInsurance && <p className='mb-0 custom-badge danger'>PENDING INSURANCE</p>}
                                    {this.props.incident?.rental?.driver?.hasIncompleteReturn && <p className='mb-0 custom-badge info'>HAS INCOMPLETE RETURN</p>}
                                    {this.props.incident?.isRecordOnly && (
                                        <>
                                            <p className='mb-0 custom-badge info' id="recordOnlyTooltip">Record Only</p>
                                            <UncontrolledTooltip target="recordOnlyTooltip">Record-Only Incident</UncontrolledTooltip>
                                        </>
                                    )}
                                </div>
                            </div>
                            <div className="case-id-section">
                                <div className="case-id-badges">
                                    <div className="custom-badge title">
                                        #{this.props.incident?.title || "--"}&nbsp;&nbsp;
                                        <CopyToClipboard textToCopy={this.props.incident?.title} displayText="case id" />
                                    </div>
                                    <div className="custom-badge pk">
                                        #{this.props.incident?.pk || "--"}&nbsp;&nbsp;
                                        <CopyToClipboard textToCopy={this.props.incident?.pk} displayText="incident id" />
                                    </div>

                                </div>
                                {(latestLiabilityClaim?.externalId || latestSubrogationClaim?.externalId) && (
                                    <div className="claim-reference-badges">
                                        {latestLiabilityClaim?.externalId && <div className="custom-badge claim-reference liability">
                                            <span className="claim-reference__label">Liability</span>&nbsp;&nbsp;
                                            <span>#{latestLiabilityClaim.externalId}</span>&nbsp;&nbsp;
                                            <CopyToClipboard textToCopy={latestLiabilityClaim.externalId} displayText="liability claim external id" />
                                        </div>}
                                        {latestSubrogationClaim?.externalId && (
                                            <div className="custom-badge claim-reference subro">
                                                <span className="claim-reference__label">Subro</span>
                                                <span>{latestSubrogationClaim.externalId}</span>&nbsp;&nbsp;
                                                <CopyToClipboard textToCopy={latestSubrogationClaim.externalId} displayText="subrogation claim external id" />
                                            </div>
                                        )}
                                    </div>
                                )}
                                <div className='action-btns-section'>
                                    {this.props.hasPermissions?.includes("archive_incident") && !this.props.incident?.isArchived &&
                                        <Button className='action-btn archive' size='sm' onClick={() => this.toggleModal("archiveIncident")}>
                                            <i className="fa fa-archive" />
                                            Archive
                                        </Button>
                                    }
                                    {!((this.props.incident?.damageTransaction?.id) || (this.props.incident?.incidentdamageSet?.edges?.length > 0)) && this.props.hasPermissions?.includes("delete_incident") &&
                                        <Button className='action-btn delete' size='sm' onClick={() => this.toggleModal("deleteIncident")}>
                                            <i className="fa fa-trash" />
                                            Delete
                                        </Button>
                                    }
                                </div>
                            </div>
                        </div>
                    </CardHeader>
                    <CardBody className="details-section">
                        <div className="details-grid">
                            <div className="detail-item">
                                <div className="detail-icon icon-purple">
                                    <i className="fa fa-user" />
                                </div>
                                <div className="detail-content">
                                    <p className="detail-label mb-0">DRIVER</p>
                                    <p className="detail-value mb-0" style={{ color: "#7c3aed" }}>
                                        {this.props.incident?.rental?.driver?.name ? (
                                            <Link to={`/driver/${this.props.incident?.rental?.driver?.id}`} target="_blank" style={{ color: "#7c3aed" }} className="incident-detail-link">
                                                {this.props.incident?.rental?.driver?.name}
                                                <i className="pl-1 fa fa-external-link" />
                                            </Link>
                                        ) : "No driver attached"}
                                    </p>
                                </div>
                            </div>
                            <div className="detail-item">
                                <div className="detail-icon icon-orange">
                                    <i className="fa fa-calendar" />
                                </div>
                                <div className="detail-content">
                                    <p className="detail-label mb-0">INCIDENT DATE</p>
                                    <p className="detail-value mb-0" style={{ color: "#ea580c" }}>
                                        {this.props.incident?.incidentDate ? (
                                            <DatetimeRenderer datetime={this.props.incident?.incidentDate} />
                                        ) : "No incident date added"}
                                    </p>
                                </div>
                            </div>
                            <div className="detail-item">
                                <div className="detail-icon icon-blue">
                                    <i className="fa fa-map-marker" />
                                </div>
                                <div className="detail-content">
                                    <p className="detail-label mb-0">LOCATION</p>
                                    <p className="detail-value mb-0" style={{ color: "#2563eb" }}>
                                        {this.props.incident?.location ? (
                                            <a
                                                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(this.props.incident.location)}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{ color: "#2563eb" }}
                                            >
                                                {this.props.incident.location}
                                                <i className="pl-1 fa fa-map-marker" />
                                            </a>
                                        ) : "No location provided"}
                                    </p>
                                </div>
                            </div>
                            <div className="detail-item">
                                <div className="detail-icon icon-green">
                                    <i className="fa fa-tasks" />
                                </div>
                                <div className="detail-content">
                                    <p className="detail-label mb-0">DATA PROGRESS</p>
                                    <p className="detail-value mb-0">
                                        <ProgressTrackingBar progress={this.props.incident?.dataProgress} missingDataFields={this.props.incident?.missingDataFields} />
                                    </p>
                                </div>
                            </div>
                        </div>
                    </CardBody>
                    {this.state.openModal === "deleteIncident" && (
                        <ConfirmationPopup
                            open
                            title="Delete Incident"
                            loading={this.props.loading}
                            message="Are you sure you want to delete this incident?"
                            handleClose={() => this.toggleModal("deleteIncident")}
                            action="Delete Incident"
                            confirmAction={() => this.deleteIncident(this.props.incident?.id)}
                            color="danger"
                            error={this.state.error}
                            success={this.state.successMessage || ""}
                        />
                    )}

                    {this.state.openModal === "archiveIncident" && !this.state.showArchiveConfirm && !this.props.incident?.isArchived && (
                        <ArchiveReasonModal
                            isOpen
                            isArchived={this.props.incident?.isArchived}
                            onCancel={() => this.toggleModal("archiveIncident")}
                            onContinue={this.handleArchiveReasonContinue}
                        />
                    )}

                    {this.state.openModal === "archiveIncident" &&
                        this.state.showArchiveConfirm && (
                            <ConfirmationPopup
                                open
                                title={this.props.incident?.isArchived ? "Un-Archive Incident" : "Archive Incident"}
                                loading={this.state.loading}
                                message={`Are you sure you want to ${this.props.incident?.isArchived ? "un-archive" : "archive"} this incident?`}
                                handleClose={() => this.toggleModal("archiveIncident")}
                                action={this.props.incident?.isArchived ? "Un-Archive Incident" : "Archive Incident"}
                                confirmAction={() => this.toggleArchiveIncident(this.props.incident?.id, this.props.incident?.isArchived)}
                                color={this.props.incident?.isArchived ? "success" : "warning"}
                                error={this.state.error}
                                success={this.state.successMessage || ""}
                            />
                        )}
                </Card>

                {this.props.incident?.isArchived && (
                    <div className="m-3 archived-incident-alert">
                        <div className="archived-incident-alert-content">
                            <p className="archived-incident-alert-title mb-0">
                                <b><i className="fa fa-archive" aria-hidden="true" /> This incident is <span style={{ color: "#d9534f" }}>archived</span> and is read-only.</b>
                            </p>
                            <div className="archived-incident-alert-reason pt-1">
                                <p className="archived-incident-alert-reason-text mb-0">
                                    <b>Archive Reason: </b>
                                    {(() => {
                                        const reason = this.fetchMetaData(this.props.incident?.metadata, "archive_reason") || "No reason provided."
                                        const words = reason.trim().split(/\s+/)
                                        const isLong = words.length > 15
                                        const { showFullArchiveReason } = this.state
                                        return (
                                            <>
                                                {isLong && !showFullArchiveReason ? `${words.slice(0, 15).join(" ")}...` : reason}
                                                {isLong && <button type="button" className="btn btn-link btn-sm p-0 ml-2" onClick={this.toggleArchiveReasonView}>{showFullArchiveReason ? "View less" : "View more"}</button>}
                                            </>
                                        )
                                    })()}
                                </p>
                            </div>
                        </div>
                        {this.props.hasPermissions?.includes("archive_incident") && (
                            <button className="btn btn-success btn-sm" onClick={() => this.toggleModal("archiveIncident")}>
                                <i className="fa fa-undo"></i>&nbsp;Un-Archive
                            </button>
                        )}
                    </div>
                )}
                {this.props.incident &&
                    <div className="incident-detail">
                        <Nav tabs>
                            {INCIDENT_DETAIL_PAGE_TABS.map(item =>
                                <NavItem key={item.id} className={this.state.tab === item.id ? "active" : "inactive"}>
                                    <NavLink className={this.state.tab === item.id ? "active" : "inactive"} onClick={() => this.toggleTab(item.id)}>
                                        {this.state.tab !== "#claims" && item.id === "#claims" && this.props.incident?.liabilityclaimSet?.edges?.length + this.props.incident?.subrogationclaimSet?.edges?.length > 0 ?
                                            <span style={{ color: "#dc3545" }}>{item.name}</span> :
                                            <>
                                                <span style={{ color: (item.id === "#tasks" && this.props.incident.openTaskCount > 0) ? "#dc3545" : "" }}>
                                                    {item.name}
                                                </span>
                                                {(this.props.incident.openTaskCount > 0 && item.id === "#tasks") && (
                                                    <span className='pending-task-count' id={"pendingTasks"}>
                                                        {this.props.incident.openTaskCount}
                                                        <UncontrolledTooltip target={"pendingTasks"}>Pending Tasks</UncontrolledTooltip>
                                                    </span>
                                                )}
                                            </>
                                        }
                                    </NavLink>
                                </NavItem>
                            )}
                        </Nav>
                        <TabContent activeTab={this.state.tab}>
                            {this.state.tab === "#overview" &&
                                <TabPane tabId="#overview">
                                    <OverviewTab currentUser={this.props.currentUser} id={this.props.incident.id} toggleTab={(id) => this.toggleTab(id)} refetchQuery={this.props.refetchQuery} />
                                </TabPane>
                            }
                            {this.state.tab === "#documents" &&
                                <TabPane tabId="#documents">
                                    <IncidentDocumentsTab loading={this.props.incidentLoading} id={this.props.incident.id} refetchQuery={this.props.refetchQuery} viewType="incident" category="INCIDENT" />
                                </TabPane>
                            }
                            {this.state.tab === "#parties" &&
                                <TabPane tabId="#parties">
                                    {this.props.hasPermissions?.includes("view_incidentparty") ?
                                        <PartiesTabContainer loading={this.props.incidentLoading} id={this.props.incident.id} refetchQuery={this.props.refetchQuery} /> :
                                        <Alert color="danger" className="mt-4">You do not have permission to view parties involved in this incident.</Alert>
                                    }
                                </TabPane>
                            }
                            {this.state.tab === "#claims" &&
                                <TabPane tabId="#claims">
                                    <ClaimsTab loading={this.props.incidentLoading} id={this.props.incident.id} refetchQuery={this.props.refetchQuery} />
                                </TabPane>
                            }
                            {this.state.tab === "#inspection" &&
                                <TabPane tabId="#inspection">
                                    <InspectionTab id={this.props.incident?.carCondition?.id} viewType="incidents" />
                                </TabPane>
                            }
                            {this.state.tab === "#dashcam" &&
                                <TabPane tabId="#dashcam">
                                    <DashcamContainer carId={this.props.incident.car.id} rentalId={this.props.incident?.rental?.id || null} incidentDate={this.props.incident?.incidentDate || null} />
                                </TabPane>
                            }
                            {this.state.tab === "#history" &&
                                <div className='info-container mt-0'>
                                    <HistoryList
                                        objectIds={Array.from(new Set([
                                            this.props.incident?.id,
                                            // ...(this.props.incident?.subrogationclaimSet?.edges?.map(({ node }) => node?.id).filter(Boolean) || []),
                                            // ...(this.props.incident?.liabilityclaimSet?.edges?.map(({ node }) => node?.id).filter(Boolean) || []),
                                            // ...(this.props.incident?.incidentpartySet?.edges?.map(({ node }) => node?.id).filter(Boolean) || []),
                                            ...(this.props.incident?.incidentdocumentSet?.edges?.map(({ node }) => node?.id).filter(Boolean) || []),
                                        ].filter(Boolean)))}
                                        emptyText="No history available"
                                    />
                                </div>
                            }
                            {this.state.tab === "#tasks" &&
                                <TabPane tabId="#tasks">
                                    <TasksTab incidentId={this.props.incident?.id} />
                                </TabPane>
                            }
                        </TabContent>
                    </div>}
            </div >
        )
    }
}

export default graphql(IncidentDetailQuery, {
    options: (props) => ({
        variables: { incidentId: props && props.params ? props.params.id : null },
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true
    }),
    props: ({ data: { incident, loading, refetch, variables, statusChoices } }) => ({
        incident,
        incidentLoading: loading,
        variables,
        statusChoices,
        refetchQuery: () => {
            return refetch({
                query: IncidentDetailQuery,
                variables: {
                    ...variables,
                },
                updateQuery: (previousResult, { fetchMoreResult }) => {
                    return { incident: fetchMoreResult.incident }
                },
            })
        },
    }),
})(
    graphql(HasPermissionsQuery, {
        options: () => ({
            variables: { userPermissions: ["archive_incident", "delete_incident", "view_incidentparty"] },
        }),
        props: ({ data: { hasPermissions, loading, variables } }) => ({
            hasPermissions,
            loading,
            variables,
        }),
    })(withApolloClient(IncidentDetailContainer)))
